import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import { UploadSimple, Warning, CheckCircle } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { AdminCrmAPI } from '../../lib/apiAdmin';
import { getApiErrorMessage } from '../../lib/apiError';
import type { ApexCrmImportReport } from '../../types/admin';

interface ImportApexCrmModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a committed run, so the board can refetch. */
  onImported: () => void;
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** One number and its label, in the preview grid. */
const Stat: React.FC<{ label: string; value: React.ReactNode; muted?: boolean }> = ({
  label,
  value,
  muted,
}) => (
  <Box>
    <Typography level="h4" sx={{ color: muted ? 'text.tertiary' : 'text.primary' }}>
      {value}
    </Typography>
    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Migrating the old ApexCRM into this pipeline.
 *
 * Two steps, never one: choosing a file previews, and only a second, explicit
 * click writes. The preview is not an estimate — the server imports into a
 * transaction and rolls it back — so what this screen shows before the commit
 * is what the commit does. That is the whole reason the flow is worth two
 * clicks rather than one.
 */
export const ImportApexCrmModal: React.FC<ImportApexCrmModalProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ApexCrmImportReport | null>(null);
  const [result, setResult] = useState<ApexCrmImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const runImport = useMutation({
    mutationFn: ({ chosen, dryRun }: { chosen: File; dryRun: boolean }) =>
      AdminCrmAPI.importApexCrm(chosen, dryRun),
    onSuccess: (report) => {
      setError(null);
      if (report.dry_run) {
        setPreview(report);
      } else {
        setResult(report);
        onImported();
      }
    },
    onError: (err: unknown) =>
      setError(getApiErrorMessage(err, 'That export could not be read.')),
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const choose = (chosen: File | null) => {
    setPreview(null);
    setResult(null);
    setError(null);
    setFile(chosen);
    if (chosen) runImport.mutate({ chosen, dryRun: true });
  };

  const report = result ?? preview;

  return (
    <Modal open={open} onClose={close}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 620 }, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <DialogTitle>Import from ApexCRM</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Upload an <code>apexcrm-backup-*.json</code> export. Contacts become leads, and their
              notes, deal history and emails land on each lead's timeline with their original dates.
              Leads are matched on email, so importing twice updates rather than duplicates.
            </Typography>

            <input
              ref={inputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(event) => choose(event.target.files?.[0] ?? null)}
            />

            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<UploadSimple size={18} />}
              onClick={() => inputRef.current?.click()}
              disabled={runImport.isPending}
            >
              {file ? file.name : 'Choose export file'}
            </Button>

            {runImport.isPending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm">
                  {result === null && preview === null ? 'Reading the export…' : 'Importing…'}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            {report && (
              <>
                <Divider />
                {result ? (
                  <Alert color="success" variant="soft" startDecorator={<CheckCircle size={18} />}>
                    Import complete.
                  </Alert>
                ) : (
                  <Typography level="title-sm">
                    Preview — nothing has been written yet
                  </Typography>
                )}

                <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                      gap: 2,
                    }}
                  >
                    <Stat label="New leads" value={report.leads_created} />
                    <Stat label="Existing leads updated" value={report.leads_updated} />
                    <Stat label="Notes" value={report.notes_imported} />
                    <Stat
                      label="Timeline entries"
                      value={report.timeline_total}
                    />
                    <Stat label="Deal stages set" value={report.stages_applied} />
                    <Stat
                      label="Pipeline (annualised)"
                      value={currency.format(Number(report.pipeline_arr))}
                    />
                    <Stat
                      label="Already imported"
                      value={report.timeline_already_present}
                      muted={report.timeline_already_present === 0}
                    />
                    <Stat
                      label="Contacts skipped"
                      value={report.leads_skipped}
                      muted={report.leads_skipped === 0}
                    />
                  </Box>
                </Sheet>

                {Object.keys(report.owners_matched).length > 0 && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Records attributed to
                    </Typography>
                    <Typography level="body-sm">
                      {Object.entries(report.owners_matched)
                        .map(([name, email]) => `${name} → ${email}`)
                        .join(' · ')}
                    </Typography>
                  </Box>
                )}

                {report.warnings.length > 0 && (
                  <Alert color="warning" variant="soft" startDecorator={<Warning size={18} />}>
                    <Box>
                      <Typography level="title-sm">
                        {report.warnings.length} thing{report.warnings.length === 1 ? '' : 's'} to know
                      </Typography>
                      <List size="sm" marker="disc" sx={{ '--ListItem-minHeight': '1.5rem' }}>
                        {report.warnings.map((warning) => (
                          <ListItem key={warning}>
                            <Typography level="body-xs">{warning}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Alert>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
              <Button variant="plain" color="neutral" onClick={close}>
                {result ? 'Done' : 'Cancel'}
              </Button>
              {preview && !result && (
                <Button
                  variant="solid"
                  loading={runImport.isPending}
                  onClick={() => file && runImport.mutate({ chosen: file, dryRun: false })}
                >
                  Import {preview.leads_created + preview.leads_updated} leads
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default ImportApexCrmModal;
